import { Warning as WarningIcon } from 'components/icons';
import { RecordLink } from 'components/record-chip/RecordLink';
import { FC, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { EditorDto } from 'store/api/editingSession';

interface ConcurrentEditingBannerProps {
    editors: EditorDto[];
    messageId?: string;
}

const formatEditorsWithLinks = (editors: EditorDto[]): ReactNode => {
    if (editors.length === 0) {
        return null;
    }

    const sortedEditors = editors.toSorted((a, b) => a.fullName.localeCompare(b.fullName));

    const links = sortedEditors.map((editor) => (
        <RecordLink
            key={editor.employeeId}
            to={`/employee/${editor.employeeId}`}
            className="font-semibold text-inherit underline"
            label={editor.fullName}
        />
    ));

    if (links.length === 1) {
        return links[0];
    } else if (links.length === 2) {
        return (
            <>
                {links[0]} and {links[1]}
            </>
        );
    } else {
        return links.map((link, index) => (
            <span key={sortedEditors[index].employeeId}>
                {link}
                {index < links.length - 1 ? ', ' : ''}
            </span>
        ));
    }
};

const ConcurrentEditingBanner: FC<ConcurrentEditingBannerProps> = ({
    editors,
    messageId = 'correspondence.concurrent-editing-warning',
}) => {
    if (editors.length === 0) {
        return null;
    }

    const namesWithLinks = formatEditorsWithLinks(editors);

    return (
        <div className="bg-warning text-grey-900 new:bg-warning-tint new:text-warning-ink flex w-full items-start gap-3 p-4">
            <WarningIcon fontSize="small" className="mt-0.5" />
            <p className="m-0 font-medium">
                <FormattedMessage id={messageId} values={{ names: namesWithLinks }} />
            </p>
        </div>
    );
};

export default ConcurrentEditingBanner;
